import { memo, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useModal } from 'providers/ModalProvider'
import { CookiePreferencesModal } from 'components/modals/CookiePreferencesModal'
import { useGetSetting } from 'api/settings'
import clsx from 'clsx'

interface ChatBottomBarProps {
  children: ReactNode
  /** 'widget' = opaque bar (default). 'page' = solid page background, seamless on the fullscreen /chat layout. */
  surface?: 'widget' | 'page'
}

const surfaceClasses: Record<NonNullable<ChatBottomBarProps['surface']>, string> = {
  widget: 'bg-background border-t border-border',
  page: 'border-t border-border bg-background',
}

const linkClass = 'bg-transparent p-0 align-baseline underline transition-opacity hover:opacity-70'

const ChatBottomBarComponent = ({ children, surface = 'widget' }: ChatBottomBarProps) => {
  const { t } = useTranslation()
  const { openModal } = useModal()
  const { setting } = useGetSetting()
  const privacyLink = setting?.privacyPolicyLink?.trim()
  const cookieLink = setting?.cookiePolicyLink?.trim()

  return (
    <div className={clsx('z-10 shrink-0 px-3 pt-3 pb-1', surfaceClasses[surface])}>
      {children}
      {/* EU AI Act art. 50 disclosure + policy links: one ultra-compact line to minimise height. */}
      <p className="mt-1 mb-0 flex flex-wrap items-center justify-center gap-x-1 text-[10px] leading-none text-muted-foreground">
        <span>{t('aiDisclaimer')}</span>
        {privacyLink && (
          <>
            <span aria-hidden>·</span>
            <a href={privacyLink} target="_blank" rel="noreferrer" className={linkClass}>
              {t('chatLinks.privacy')}
            </a>
          </>
        )}
        {cookieLink && (
          <>
            <span aria-hidden>·</span>
            <a href={cookieLink} target="_blank" rel="noreferrer" className={linkClass}>
              {t('chatLinks.cookie')}
            </a>
            <span aria-hidden>·</span>
            <button type="button" onClick={() => openModal(<CookiePreferencesModal />)} className={linkClass}>
              {t('chatLinks.preferences')}
            </button>
          </>
        )}
      </p>
    </div>
  )
}

export const ChatBottomBar = memo(ChatBottomBarComponent)
ChatBottomBar.displayName = 'ChatBottomBar'
